import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ExchangeRateDto } from 'src/generated/services/TFinancialApi';
import {
  ExchangeRateFilters,
  useDeleteExchangeRate,
  useExchangeRateList,
  useExchangeRatesMetadata,
  useUpsertExchangeRate,
} from 'src/use-cases/settings/useExchangeRates';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { useTableEntityHandler } from 'src/presentations/hooks/useTableEntityHandler';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import Button from 'src/presentations/components/atom/button';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { SettingsMain } from '../SettingsMain';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
  width: '100px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

interface ExchangeRateTableProps {
  searchQuery?: string;
}

export const ExchangeRateMain: FC<ExchangeRateTableProps> = (props): JSX.Element => {
  const { searchQuery } = props;
  const [rowToDelete, setRowToDelete] = useState<ExchangeRateDto | null>(null);
  const [filters, setFilters] = useState<ExchangeRateFilters>({});

  // Track whether this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use the enhanced table hook
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps, validateItem, clearValidationErrors, getRequestParams } =
    useEnhancedTable<ExchangeRateDto>({
      initialSorting: [
        {
          id: 'exchangeRate',
          desc: false,
        },
      ],
      onSearchQueryChange: searchQuery,
      requiredFields: ['baseCurrency', 'toCurrency', 'exchangeRate'],
    });

  const { mutateAsync: upsertExchangeRate } = useUpsertExchangeRate();
  const { mutateAsync: deleteExchangeRate } = useDeleteExchangeRate();

  // No need to track time field types anymore as we use direct backend fields
  let sortingField = sorting.length > 0 ? sorting[0].id : undefined;
  let sortingDirection: 'ASC' | 'DESC' | undefined;

  if (sorting.length > 0) {
    sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';

    // Only map 'rate' to 'exchangeRate' if needed
    if (sortingField === 'rate') {
      sortingField = 'exchangeRate';
    }
  }

  // Fetch exchange rates data
  const { data, isLoading, refetch } = useExchangeRateList(
    {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortingColum: sortingField, // Updated to match API parameter name
      sortingDirection,
    },
    filters
  );

  // Get metadata for filter options with current filters
  const { data: metadataData, refetch: refetchMetadata } = useExchangeRatesMetadata(filters);

  // Refetch data when filters change
  useEffect(() => {
    if (isInitialLoad) {
      // On initial load, don't trigger a refetch with empty filters
      setIsInitialLoad(false);
    } else {
      // For subsequent filter changes, refetch data
      refetch();
    }
  }, [filters, refetch, isInitialLoad, setIsInitialLoad]);

  // Memoize exchangeRates to avoid recreating on every render
  const exchangeRates = useMemo(() => {
    const content = data?.data?.content || [];

    // Use API data as-is without special mappings
    return content;
  }, [data]);
  const exchangeRatesTotal = data?.data?.totalElements || 0;

  // We no longer need to detect time fields since we're using direct fields from the backend

  // Update a specific filter field
  const updateFilter = useCallback(
    <K extends keyof ExchangeRateFilters>(key: K, value: ExchangeRateFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setFilters]
  );

  // Handler for table header sorting
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  // Handler for table filters
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      // If all options are selected or none are selected, clear the filter
      const isAllSelected = allOptions && selectedValues.length === allOptions.length;

      if (!selectedValues.length || isAllSelected) {
        // Clear the filter for this column
        switch (columnId) {
          case 'baseCurrency':
            updateFilter('baseCurrencies', undefined);
            break;
          case 'toCurrency':
            updateFilter('toCurrencies', undefined);
            break;
          case 'exchangeRate':
            updateFilter('exchangeRates', undefined);
            break;
          case 'timeBase':
            updateFilter('timeBases', undefined);
            break;
          case 'time':
            updateFilter('times', undefined);
            break;
          default:
            break;
        }

        // Critical: Always refresh metadata and data after clearing filters
        refetchMetadata();
        refetch();
        return;
      }

      // Apply the filter based on column ID
      switch (columnId) {
        case 'baseCurrency':
          updateFilter('baseCurrencies', selectedValues);
          break;
        case 'toCurrency':
          updateFilter('toCurrencies', selectedValues);
          break;
        case 'exchangeRate':
          updateFilter('exchangeRates', selectedValues);
          break;
        case 'timeBase':
          updateFilter('timeBases', selectedValues);
          break;
        case 'time':
          updateFilter('times', selectedValues);
          break;
        default:
          break;
      }
    },
    [updateFilter, refetchMetadata, refetch]
  );

  const columns = useMemo(
    (): MRT_ColumnDef<ExchangeRateDto>[] => [
      // ID column - hidden but available for sorting
      {
        accessorKey: 'id',
        header: 'ID',
        enableSorting: false,
        enableColumnFilter: false,
      },
      // Enable sorting on all columns
      {
        accessorKey: 'baseCurrency',
        header: 'Base Currency',
        muiTableHeadCellProps: {
          sx: { width: '130px' },
        },
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.baseCurrencies);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.baseCurrencies}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('baseCurrency', { required: true }),
      },
      {
        accessorKey: 'toCurrency',
        header: 'To Currency',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { width: '130px' },
        },
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.toCurrencies);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.toCurrencies}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('toCurrency', { required: true }),
      },
      {
        accessorKey: 'exchangeRate',
        header: 'Rate',
        enableSorting: false,
        id: 'exchangeRate', // This ensures it works with both 'rate' and 'exchangeRate' identifiers
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.exchangeRates);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.exchangeRates}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('exchangeRate', { required: true, type: 'number' }),
      },
      // Time Based column - shows the type of time period being used
      {
        accessorKey: 'timeBase',
        header: 'Time Based',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.timeBases);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.timeBases}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('timeBase'),
      },

      // Time column - displays the actual time value
      {
        accessorKey: 'time',
        header: 'Time',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.times);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.times}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('time'),
      },

      // Day column (hidden)
      {
        accessorKey: 'day',
        header: 'Day',
        enableSorting: true,
      },

      // Month column (hidden)
      {
        accessorKey: 'month',
        header: 'Month',
        enableSorting: true,
      },

      // Quarter column (hidden)
      {
        accessorKey: 'quarter',
        header: 'Quarter',
        enableSorting: true,
      },
    ],
    [
      getTextFieldProps,
      handleTableSort,
      handleTableFilter,
      metadataData?.data,
      filters.baseCurrencies,
      filters.toCurrencies,
      filters.exchangeRates,
      filters.timeBases,
      filters.times,
    ]
  );

  // Function to prepare the entity for upsert operation
  const prepareEntityForUpsert = useCallback((values: Record<string, any>, originalData?: any): ExchangeRateDto => {
    // Extract all values directly
    const { id, baseCurrency, toCurrency, exchangeRate, day, month, quarter, timeBase, time } = values;

    const emptyToNull = (value: string | null | undefined): string | null => {
      if (value === undefined || value === null || value === '') {
        return null;
      }
      return value;
    };

    // Initialize time-based fields
    let dayValue = emptyToNull(day);
    let monthValue = emptyToNull(month);
    let quarterValue = emptyToNull(quarter);

    // Map time value to the appropriate field based on timeBase
    if (time && timeBase) {
      const timeValue = emptyToNull(time);

      switch (timeBase.toLowerCase()) {
        case 'day':
          dayValue = timeValue;
          monthValue = null;
          quarterValue = null;
          break;
        case 'month':
          dayValue = null;
          monthValue = timeValue;
          quarterValue = null;
          break;
        case 'quarter':
          dayValue = null;
          monthValue = null;
          quarterValue = timeValue;
          break;
        default:
          // If timeBase doesn't match expected values, keep original values
          break;
      }
    }

    // Return the DTO with fields directly from form values
    const dto: ExchangeRateDto = {
      id: originalData?.id || id || undefined,
      baseCurrency: emptyToNull(baseCurrency),
      toCurrency: emptyToNull(toCurrency),
      exchangeRate: Number(exchangeRate),
      day: dayValue,
      month: monthValue,
      quarter: quarterValue,
      timeBase: emptyToNull(timeBase),
    };

    return dto;
  }, []);

  // Use the standardized entity handler hook
  const {
    handleCreate: handleCreateExchangeRate,
    handleUpdate: handleUpdateExchangeRate,
    handleDelete: handleDeleteExchangeRate,
    isLoading: isLoadingTableEntity,
  } = useTableEntityHandler<ExchangeRateDto>(
    {
      entityName: 'Exchange Rate',
      upsertEntity: upsertExchangeRate,
      deleteEntity: deleteExchangeRate,
      prepareEntityForUpsert,
    },
    validateItem
  );

  // Render row actions for exchange rates
  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  const baseTableConfig = createTableConfig<ExchangeRateDto>({
    hiddenColumns: ['id', 'day', 'month', 'quarter'],
    onCreatingCancel: clearValidationErrors,
    onEditingCancel: clearValidationErrors,
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    onCreatingRowSave: handleCreateExchangeRate,
    onEditingRowSave: handleUpdateExchangeRate,
    data: exchangeRates,
    columns,
    state: {
      sorting,
      isLoading,
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        // grow: false,
        muiTableHeadCellProps: {
          sx: { width: '80px' },
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
    renderRowActions: ({ row, table }) => renderRowActions({ row, table }),
  });

  // Using BaseTableWithPagination to render the table
  return (
    <SettingsMain
      title='Exchange Rates'
      subtitle='Setup the exchange rates'
      backPath='/settings'
      topActions={
        <Button
          variant='primary'
          className='!pr-12 !pl-16 !ml-12'
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          <div className='flex items-center gap-10'>
            <span className='text-14 xl:text-16'>Add New</span>
            <AddIcon />
          </div>
        </Button>
      }
    >
      <SettingsBaseTable
        table={table}
        totalItems={exchangeRatesTotal}
        pagination={pagination}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
      />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isLoadingTableEntity}
        onAccept={async () => {
          await handleDeleteExchangeRate({ original: rowToDelete });
          setRowToDelete(null);
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure want to delete this exchange rate?</p>
      </ModalConfirm>
    </SettingsMain>
  );
};
